/** ************************************************************************
 * Atomic Component for the bPerx iFrame UI.
 *
 * Atomic Type: Offer Opt Control, Checkbox
 * Purpose: Control whether or not a customer is opting into the offer
 *
 * This opt-checkbox controls whether or not the customer/user is opting
 * into the offer. It receives a default value of true, unless the
 * partner specifies a value via customization.
 *
 * Please note the visibility of opt-checkbox can be partner-customized.
 * While bPerx would prefer the opt-checkbox to be
 * -- 1 default opted in
 * -- 2 only visible when offer items are visible
 * Both of these can be changed by partner customization.
 *
 * Note that if a user opts out (clicking the checkbox and turning it off)
 * then if the products are currently visible, they will become hidden.
 * Users can see products again by clicking the "Change My Selection" button.
 *
 * @file   Atomic Component, offer opt control, checkbox
 * @author Paul J Stales
 * @since  2.2.0
 ************************************************************************ */

import { FunctionalComponent, h } from 'preact'
import { formElementValueCalculator } from '../../../../../../common/utilities/FormElementValueCalculator'
import { TransactionInformation } from '../../../../../../common/types/transactionInformationTypes/TransactionInformation'
import { css } from 'aphrodite'
import { IFRAME_CLASSNAMES } from '../../../../../../common/constants/ConstantsIFrameClassNames'
import { DisplayEvent } from '../../../../../../common/types/reportableEvents/subTypes/DisplayEvent'
import { ReportableEventType } from '../../../../../../common/types/reportableEvents/ReportableEvent'

/**
 * Props Interface for Checkbox
 */
interface OfferOptCheckboxProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: { [key: string]: any }
  isOptElementAlwaysVisible?: boolean
  areProductsVisible: boolean
  isIntentToOptIn: boolean
  optTextString: string
  transactionInformation: TransactionInformation
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
  displayEvent: DisplayEvent
  setDisplayEvent: (displayEvent: DisplayEvent) => void
  displayEventHandler: (
    displayEvent: DisplayEvent,
    setDisplayEvent: (displayEvent: DisplayEvent) => void,
    newTransactionInformation: TransactionInformation,
    reportableEventType: string
  ) => void
}

/**
 * Component Opt-Checkbox toggles the customer's isOptingIn status for the offer.
 *
 * Note that this checkbox has an "INTENT", meaning "if this checkbox is
 * checked, what does it mean?".
 * @param CheckboxProps
 * @returns
 */
const OfferOptCheckbox: FunctionalComponent<OfferOptCheckboxProps> = ({
  styles,
  isOptElementAlwaysVisible,
  areProductsVisible,
  isIntentToOptIn,
  optTextString,
  transactionInformation,
  setTransactionInformation,
  setDoesPartnerNeedUpdateForTransactionInformation,
  displayEvent,
  setDisplayEvent,
  displayEventHandler
}: OfferOptCheckboxProps) => {
  // if INTENT matches OPT_IN_STATUS, CHECKED
  const IS_CHECKED = formElementValueCalculator(
    isIntentToOptIn,
    transactionInformation.customerChoice.isOptingIn
  )

  return (
    <div
      class={`${IFRAME_CLASSNAMES.BPERX_OPT_CHECKBOX_CONTAINER}${' '}${
        styles[IFRAME_CLASSNAMES.BPERX_OPT_CHECKBOX_CONTAINER] === undefined
          ? ''
          : css(styles[IFRAME_CLASSNAMES.BPERX_OPT_CHECKBOX_CONTAINER])
      }`}
    >
      {(isOptElementAlwaysVisible || areProductsVisible) && (
        <div>
          <label
            htmlFor={IFRAME_CLASSNAMES.BPERX_LABEL_OPT_CHECKBOX}
            class={`${IFRAME_CLASSNAMES.BPERX_LABEL_OPT_CHECKBOX}${' '}${
              styles[IFRAME_CLASSNAMES.BPERX_LABEL_OPT_CHECKBOX] === undefined
                ? ''
                : css(styles[IFRAME_CLASSNAMES.BPERX_LABEL_OPT_CHECKBOX])
            }`}
          >
            {optTextString}
          </label>
          <input
            type="checkbox"
            name=""
            class={`${IFRAME_CLASSNAMES.BPERX_OPT_CHECKBOX}${' '}${
              styles[IFRAME_CLASSNAMES.BPERX_OPT_CHECKBOX] === undefined
                ? ''
                : css(styles[IFRAME_CLASSNAMES.BPERX_OPT_CHECKBOX])
            }`}
            checked={IS_CHECKED}
            onChange={(e) => {
              const newTransactionInformation: TransactionInformation =
                transactionInformationHandler(
                  transactionInformation,
                  setTransactionInformation,
                  setDoesPartnerNeedUpdateForTransactionInformation
                )
              displayEventHandler(
                displayEvent,
                setDisplayEvent,
                newTransactionInformation,
                newTransactionInformation.customerChoice.isOptingIn
                  ? ReportableEventType.DISPLAY_CUSTOMER_OPTED_IN_EVENT
                  : ReportableEventType.DISPLAY_CUSTOMER_OPTED_OUT_EVENT
              )
            }}
          />
        </div>
      )}
    </div>
  )
}

export default OfferOptCheckbox

/**
 * Create a new TransactionInformation with the changes to opt status,
 * and execute the setTransactionInformation useState update function.
 *
 * There is a useEffect triggered by this that will update the
 * parent/partner window about the updated TransactionInformation.
 *
 * @param transactionInformation
 * @param setTransactionInformation
 * @param setDoesPartnerNeedUpdateForTransactionInformation
 * @returns
 */
function transactionInformationHandler(
  transactionInformation: TransactionInformation,
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void,
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
): TransactionInformation {
  const newTransactionInformation: TransactionInformation = {
    ...transactionInformation
  }

  newTransactionInformation.customerChoice.isOptingIn =
    !newTransactionInformation.customerChoice.isOptingIn
  setTransactionInformation(newTransactionInformation)
  setDoesPartnerNeedUpdateForTransactionInformation(true)
  return newTransactionInformation
}
